import {
  Component,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core'
import { ConnectedPosition, FlexibleConnectedPositionStrategy, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay'
import { TemplatePortal } from '@angular/cdk/portal'
import { merge, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

@Directive({
  selector: '[linChatListTrigger]',
  standalone: false,
})
export class ChatListTriggerDirective implements OnDestroy {
  private overlayRef: OverlayRef
  private portal: TemplatePortal
  private destroy$ = new Subject<void>()

  @HostListener('keydown')
  onKeyDown() {
    // hot key for trigger show/hide chat list
  }

  @HostListener('click')
  onClick() {
    this.openList()
  }

  @Input('linChatListTrigger') listComponent: Component & { handleClose: EventEmitter<void>; templateRef: TemplateRef<unknown> }

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private hostElement: ElementRef,
  ) {}

  openList(): void {
    const portalRef = this.createPortal()

    this.overlayRef = this.createOverlay()
    this.overlayRef.attach(portalRef)
    this.attachClosingEvents()
  }

  ngOnDestroy(): void {
    this.overlayRef?.detach()

    if (this.portal?.isAttached) {
      this.portal.detach()
    }

    this.destroy$.next()
    this.destroy$.complete()
  }

  private attachClosingEvents() {
    merge(this.overlayRef.backdropClick(), this.listComponent.handleClose)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => void this.overlayRef.detach())
  }

  private createOverlay(): OverlayRef {
    const config = this.getOverlayConfig()

    return this.overlay.create(config)
  }

  private getOverlayConfig(): OverlayConfig {
    return new OverlayConfig({
      positionStrategy: this.getOverlayPositionStrategy(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    })
  }

  private getOverlayPositionStrategy(): FlexibleConnectedPositionStrategy {
    const belowPosition: ConnectedPosition = {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    }

    return this.overlay.position().flexibleConnectedTo(this.hostElement).withPositions([belowPosition])
  }

  private createPortal(): TemplatePortal<unknown> {
    this.portal = new TemplatePortal(this.listComponent.templateRef, this.viewContainerRef)
    return this.portal
  }
}
